import { SentryApiClient } from '_generated/api';

export const SiteUIThemeTypeNames: Record<
  SentryApiClient.UIThemeType,
  string
> = {
  [SentryApiClient.UIThemeType.Default]: 'Default',
  [SentryApiClient.UIThemeType.InfluentMonitoring]: 'Influent Monitoring',
  [SentryApiClient.UIThemeType.PreTreatment]: 'Pre-Treatment',
  [SentryApiClient.UIThemeType.BioOptimization]: 'BNR/Bio-P',
  [SentryApiClient.UIThemeType.AnaerobicDigestion]: 'AD/Co-digestion',
};

/**
 * Theme color configuration based on the "4 Paths to Growth" style guide.
 * Each theme has a primary color and tints at 75%, 50%, and 25% opacity.
 */
export interface SiteThemeColors {
  primary: string;
  primary75: string;
  primary50: string;
  primary25: string;
}

export interface SiteThemeConfig {
  name: string;
  productName: string;
  colors: SiteThemeColors;
  iconPath: string;
  /** CSS-friendly identifier for data attributes */
  cssId: string;
}

const THEME_CONFIGS: Record<
  SentryApiClient.UIThemeType,
  SiteThemeConfig | null
> = {
  [SentryApiClient.UIThemeType.Default]: null,
  [SentryApiClient.UIThemeType.InfluentMonitoring]: {
    name: 'Influent Monitoring',
    productName: 'BioShield',
    colors: {
      primary: '#0071BC',
      primary75: '#4095CD',
      primary50: '#80B8DE',
      primary25: '#BFDCEE',
    },
    iconPath: '/influent-monitoring-icon.png',
    cssId: 'influent-monitoring',
  },
  [SentryApiClient.UIThemeType.PreTreatment]: {
    name: 'Pre-Treatment',
    productName: 'BioAlert',
    colors: {
      primary: '#FF6666',
      primary75: '#FF8C8C',
      primary50: '#FFB3B3',
      primary25: '#FFD9D9',
    },
    iconPath: '/pre-treatment-icon.png',
    cssId: 'pre-treatment',
  },
  [SentryApiClient.UIThemeType.BioOptimization]: {
    name: 'BNR/Bio-P',
    productName: 'CarbonSense',
    colors: {
      primary: '#339999',
      primary75: '#66B2B2',
      primary50: '#99CCCC',
      primary25: '#CCE6E6',
    },
    iconPath: '/bio-optimization-icon.png',
    cssId: 'bio-optimization',
  },
  [SentryApiClient.UIThemeType.AnaerobicDigestion]: {
    name: 'AD/Co-digestion',
    productName: 'DigesterSense',
    colors: {
      primary: '#FBB03B',
      primary75: '#FCC46C',
      primary50: '#FDD89D',
      primary25: '#FEEBCE',
    },
    iconPath: '/anaerobic-digestion-icon.png',
    cssId: 'anaerobic-digestion',
  },
};

/**
 * Get the theme configuration for a given theme type.
 * Returns null for Default theme.
 */
export function getThemeConfig(
  theme: SentryApiClient.UIThemeType | undefined | null
): SiteThemeConfig | null {
  if (theme == null || theme === SentryApiClient.UIThemeType.Default) {
    return null;
  }
  return THEME_CONFIGS[theme] ?? null;
}

/**
 * Check if a theme is a styled theme (not Default).
 */
export function isStyledTheme(
  theme: SentryApiClient.UIThemeType | undefined | null
): boolean {
  return getThemeConfig(theme) !== null;
}
